// v2/types/domain.ts — Core domain enums and entity interfaces

// ─── Enums ───────────────────────────────────────────────────────────────────

export enum Role {
  ADMIN = 'ADMIN',
  SALES_USER = 'SALES_USER',
  BACK_OFFICE_USER = 'BACK_OFFICE_USER',
  FIELD_USER = 'FIELD_USER',
  SALES_MANAGER = 'SALES_MANAGER',
}

export enum ProductType {
  PERSONAL_LOAN = 'PERSONAL_LOAN',
  BUSINESS_LOAN = 'BUSINESS_LOAN',
  CONSUMER_DURABLES_LOAN = 'CONSUMER_DURABLES_LOAN',
  GROUP_LOAN = 'GROUP_LOAN',
}

export enum ApplicationState {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  SOFT_APPROVED = 'SOFT_APPROVED',
  APPROVED = 'APPROVED',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  VERIFICATION_COMPLETE = 'VERIFICATION_COMPLETE',
  INVOICE_COLLECTED = 'INVOICE_COLLECTED',
  MANDATED = 'MANDATED',
  SIGNED = 'SIGNED',
  DISBURSED = 'DISBURSED',
  REJECTED = 'REJECTED',
}

export enum Action {
  VIEW_APPLICATION = 'VIEW_APPLICATION',
  CREATE_APPLICATION = 'CREATE_APPLICATION',
  EDIT_APPLICATION = 'EDIT_APPLICATION',
  SAVE_APPLICATION = 'SAVE_APPLICATION',
  SUBMIT_APPLICATION = 'SUBMIT_APPLICATION',
  UPLOAD_DOCUMENT = 'UPLOAD_DOCUMENT',
  SOFT_APPROVE = 'SOFT_APPROVE',
  REQUEST_EDIT_APPLICATION = 'REQUEST_EDIT_APPLICATION',
  REJECT_APPLICATION = 'REJECT_APPLICATION',
  SANCTION = 'SANCTION',
  START_VERIFICATION = 'START_VERIFICATION',
  INVOICE_COLLECTED = 'INVOICE_COLLECTED',
  MANDATE = 'MANDATE',
  SIGN = 'SIGN',
  DISBURSE = 'DISBURSE',
  REOPEN_APPLICATION = 'REOPEN_APPLICATION',
  VIEW_USER_LOGIN_HISTORY = 'VIEW_USER_LOGIN_HISTORY',
}

export enum TaskStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  CANCELLED = 'CANCELLED',
}

export enum TaskType {
  ACTION = 'ACTION',
  APPROVAL = 'APPROVAL',
  SIMPLE_TASK = 'SIMPLE_TASK',
  LEAD_FOLLOW_UP = 'LEAD_FOLLOW_UP',
  VERIFICATION = 'VERIFICATION',
  PRE_APPROVED_DOCUMENT_COLLECTION = 'PRE_APPROVED_DOCUMENT_COLLECTION',
}

// ─── Entity Interfaces ───────────────────────────────────────────────────────

export interface Lead {
  phoneNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  address?: {
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    state: string;
    postalCode: string;
  };
  source: {
    referral: string;
    channel: string;
  };
  metaData?: {
    memberOf?: {
      id: string;
      name: string;
    };
  };
}

export interface Address {
  addressLineOne: string;
  addressLineTwo?: string;
  city: string;
  state: string;
  postalCode: string;
  type?: string;
}

export interface CoApplicant {
  relationToApplicant: string;
  personalDetails: {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    email?: string;
  };
  kycDetails?: {
    panNumber?: string;
  };
  addressId: string[];
}

export interface Reference {
  relationToApplicant: string;
  personalDetails: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
  };
  addressId: string;
}

export interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  accountType: string;
  branchName?: string;
}

export interface Document {
  id: string;
  fileName: string;
  fileCategory: string;
  fileUrl: string;
  uploadedAt: string;
  uploadedBy: string;
}

export interface Application {
  id: string;
  leadId: string;
  productType: ProductType;
  state: ApplicationState;
  applicantDetails: Record<string, unknown>;
  kycDetails: Record<string, unknown>;
  personalDetails: Record<string, unknown>;
  loanRequirementDetails: Record<string, unknown>;
  incomeDetails: Record<string, unknown>;
  addresses: Address[];
  coApplicantDetails: CoApplicant[];
  references: Reference[];
  bankAccounts: BankAccount[];
  membershipDetails?: Record<string, unknown>;
  documents: Document[];
  rejectionReason?: string;
  createdAt?: string;
  createdTime?: string;
  updatedAt?: string;
  createdBy: string;
  // Fields from /application/summary endpoint (not present on full application)
  customerName?: string;
  product?: string;
}

export interface Task {
  taskId: string;
  title: string;
  description: string;
  assignee: string;
  dueDateInUTCEpoch: number;
  createDateInUTCEpoch: number;
  updateDateInUTCEpoch: number;
  priority: number;
  type: string;
  status: TaskStatus;
  resourceType: string;
  resourceId: string;
  closureReason?: string;
  author: string;
  updatedBy?: string;
  tenantId?: string;
  watchers?: string[];
  labels?: string[];
  isBlocked?: boolean;
  contextData?: Record<string, string>;
}

export interface GroupMember {
  id: string;
  contactId: string;
  name: string;
  phone: string;
  role: 'COORDINATOR' | 'MEMBER';
  status: string;
}

export interface Group {
  id: string;
  name: string;
  city: string;
  coordinatorId: string;
  bdManagerName: string;
  bdManagerPhone: string;
  type?: string;
  description?: string;
  members: GroupMember[];
  createdAt: string;
  updatedAt: string;
}

export interface ResourceNote {
  id: string;
  resourceType: 'lead' | 'application';
  resourceId: string;
  content: string;
  author: string;
  createdAt: string;
  urls?: string[];
}

export interface UserProfile {
  userName: string;
  email: string;
  role: Role;
}

export interface LoginEvent {
  timestamp: string;
  location?: { lat: number; lng: number };
  deviceInfo?: string;
}
